import { Inject, Injectable } from "@angular/core";
import { EMPTY, catchError, from, map, mergeMap, of, switchMap, tap } from "rxjs";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import * as appointmentsActions from "./calendar.actions";
import * as invoicesActions from "../../office/invoice-ngrx/invoices.actions";
import * as studentsActions from "../../students/ngrx/students.actions";
import * as errorActions from "../../core/error/ngrx/error.actions";
import { DataService } from "src/app/supabase/services/data.service";
import {
  sendAppointmentReminder,
  addCalendarResources,
  getLocalEvents,
} from "./calendar.actions";
import { ToastController } from "@ionic/angular";
import { GcalendarService } from "src/app/shared/services/gcalendar.service";
import { getCalendarResources } from './calendar.actions';
import { updateCalendarResource } from './calendar.actions';
import { getGoogleEvents } from "./calendar.actions";
import { LocalCalendarService } from "src/app/shared/services/localcalendar.service";

@Injectable()
export class CalendarEffects {
  constructor(
    private actions$: Actions,
    private dataService: DataService,
    private gcalendarService: GcalendarService,
    private localCalendarService: LocalCalendarService,
    private toastController: ToastController
  ) {}

  loadAppointments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appointmentsActions.loadAppointments),
      mergeMap(() =>
        from(this.dataService.getAppointments()).pipe(
          mergeMap((appointments) => [
            invoicesActions.upsertInvoices({
              invoices: appointments.filter(appointment => appointment.Invoice !== null).map((appointment) => appointment.Invoice),
            }),
            studentsActions.upsertStudents({
              students: appointments.filter(appointment => appointment.Student !== null).map((appointment) => appointment.Student),
            }),
            appointmentsActions.loadAppointmentsSuccess({
              appointments: appointments.map((appointment) => ({
                ...appointment,
                Student: undefined,
                Invoice: undefined,
              })),
            }),
          ]),
          catchError((error) => of(errorActions.handleError({error: "Die Termine konnten nicht geladen werden.", showToast: false, exception: error})))
        )
      )
    )
  );

  loadUpcomingLessons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appointmentsActions.loadUpcomingLessons),
      mergeMap(() =>
        from(this.dataService.getUpcomingLessons()).pipe(
          mergeMap((lessons) => {
            return [
              invoicesActions.upsertInvoices({
                invoices: lessons.filter(lesson => lesson.Invoice !== null).map((lesson) => lesson.Invoice),
              }),
              studentsActions.upsertStudents({
                students: lessons.filter(lesson => lesson.Student !== null).map((lesson) => lesson.Student),
              }),
              appointmentsActions.loadUpcomingLessonsSuccess({
                lessons: lessons.map((lesson) => ({
                  ...lesson,
                  Student: undefined,
                  Invoice: undefined,
                })),
              }),
            ];
          }),
          catchError((error) => of(errorActions.handleError({error: "Die Termine konnten nicht geladen werden.", showToast: false, exception: error})))
        )
      )
    )
  );

  addAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appointmentsActions.addLesson),
      mergeMap(async ({ lesson, googleCalendarId, localCalendarConnected }) => {
        let updatedLesson = { ...lesson };
        if (googleCalendarId) {
          const googleLesson = {
            summary: lesson.subject,
            description: lesson.notes,
            start: {
              dateTime: new Date(lesson.start_time).toISOString(),
              timeZone: "Europe/Berlin"
            },
            end: {
              dateTime: new Date(lesson.end_time).toISOString(),
              timeZone: "Europe/Berlin"
            },
            location: lesson.location
          };
          try {
            const google_event = await this.gcalendarService.createGoogleEvent(
              googleCalendarId,
              googleLesson
            );
            updatedLesson = { ...lesson, google_id: google_event.id };
          } catch (error) {
            console.error(error);
          }
        } if (localCalendarConnected) {
          const localLesson = {
            title: lesson.subject,
            location: lesson.location,
            startDate: new Date(lesson.start_time).getTime(),
            endDate: new Date(lesson.end_time).getTime(),
          };
          try {
            const local_event = await this.localCalendarService.createLocalEvent(
              localLesson
            );
            updatedLesson = { ...lesson, local_calendar_id: local_event };
          } catch (error) {
            console.error(error);
          }
        }
        return updatedLesson;
      }),
      mergeMap((lesson) => 
        from(this.dataService.addLesson(lesson)).pipe(
          map((addedLesson) => appointmentsActions.addLessonSuccess({ lesson: addedLesson })),
          catchError((error) => of(errorActions.handleError({error: "Der Termin konnte nicht erstellt werden.", showToast: true, exception: error})))
        )
      )
    )
  );

  deleteAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appointmentsActions.deleteAppointment),
      mergeMap(({ appointmentId, googleCalendarId, googleId, localCalendarId }) => {
        if (googleCalendarId && googleId) {
          try {
            this.gcalendarService.deleteGoogleEvent(googleCalendarId, googleId);
          } catch (error) {
            of(errorActions.handleError({error: "Der Google Termin konnte nicht gelöscht werden.", showToast: false, exception: error}));
          }
        } if (localCalendarId) {
          try {
            this.localCalendarService.deleteLocalEvent(localCalendarId);
          } catch (error) {
            tap(async () => {
              const toast = await this.toastController.create({
                message: "Der lokale Termin konnte nicht gelöscht werden. Nutzt du Quick Drive auf mehreren Geräten? Dann empfehlen wir dir, die Termine anders zu synchronisieren.",
                duration: 2000,
                color: "danger",
              });
              toast.present();
            });
          }
        }
        return from(this.dataService.deleteAppointment(appointmentId)).pipe(
          map((appointment) => appointmentsActions.deleteAppointmentSuccess({ appointment })),
          catchError((error) => {
            return of(errorActions.handleError({ error: "Der Termin konnte nicht gelöscht werden.", showToast: true, exception: error }));
          })
        )
      })
    )
  );

  updateAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appointmentsActions.updateLesson),
      mergeMap(({ lesson, googleCalendarId }) => {
        if (googleCalendarId) {
          const googleLesson = {
            summary: lesson.subject,
            description: lesson.notes,
            start: {
              dateTime: new Date(lesson.start_time).toISOString(),
              timeZone: "Europe/Berlin"
            },
            end: {
              dateTime: new Date(lesson.end_time).toISOString(),
              timeZone: "Europe/Berlin"
            },
            location: lesson.location
          };
          this.gcalendarService.updateGoogleEvent(
            googleCalendarId,
            lesson.google_id,
            googleLesson
          );
        } if (lesson.local_calendar_id) {
            const eventId = lesson.local_calendar_id;
            const localLesson = {
              title: lesson.subject,
              location: lesson.location,
              startDate: new Date(lesson.start_time).getTime(),
              endDate: new Date(lesson.end_time).getTime(),
            };
            try {
              this.localCalendarService.updateLocalEvent(
                eventId,
                localLesson
              );
            } catch (error) {
              of(errorActions.handleError({error: "Der lokale Termin konnte nicht aktualisiert werden. Nutzt du Quick Drive auf mehreren Geräten? Dann empfehlen wir dir, die Termine anders zu synchronisieren.", showToast: true, exception: error}));
            }
        }
        return from(this.dataService.updateLesson(lesson)).pipe(
          map((lesson) => appointmentsActions.updateLessonSuccess({ lesson })),
          catchError((error) => of(errorActions.handleError({error: "Der Termin konnte nicht aktualisiert werden.", showToast: true, exception: error})))
        )
      })
    )
  );

  sendAppointmentReminder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendAppointmentReminder),
      mergeMap(({ sendAppointmentRequest }) =>
        from(
          this.dataService.sendAppointmentReminder(sendAppointmentRequest)
        ).pipe(
          tap(async () => {
            const toast = await this.toastController.create({
              message: "Terminbestätigung gesendet",
              duration: 2000,
              color: "success",
            });
            toast.present();
          }),
          map(() =>
            appointmentsActions.sendAppointmentReminderSuccess({ sendAppointmentRequest })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Terminbestätigung konnte nicht gesendet werden.", showToast: true, exception: error})))
        )
      )
    )
  );

  addCalendarResources$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addCalendarResources),
      mergeMap(({ resources }) =>
        from(this.dataService.addCalendarResources(resources)).pipe(
          map((resources) =>
            appointmentsActions.addCalendarResourcesSuccess({ resources })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Kalenderressourcen konnten nicht hinzugefügt werden.", exception: error})))
        )
      )
    )
  );

  getCalendarResources$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCalendarResources),
      switchMap(() =>
        from(this.dataService.getCalendarResources()).pipe(
          map((resources) =>
            appointmentsActions.getCalendarResourcesSuccess({ resources })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Kalenderressourcen konnten nicht geladen werden.", showToast: false, exception: error})))
        )
      )
    )
  );

  updateCalendarResource$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCalendarResource),
      mergeMap(({ resource }) =>
        from(this.dataService.updateCalendarResource(resource)).pipe(
          map((resource) =>
            appointmentsActions.updateCalendarResourceSuccess({ resource })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Kalenderressource konnte nicht aktualisiert werden.", exception: error})))
        )
      )
    )
  );

  getLocalEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getLocalEvents),
      mergeMap(({ calendarId }) =>
        from(this.localCalendarService.getLocalEvents(calendarId)).pipe(
          map((events) =>
            appointmentsActions.getLocalEventsSuccess({ events })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die lokalen Termine konnten nicht geladen werden.", showToast: false, exception: error})))
        )
      )
    )
  );

  getGoogleEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getGoogleEvents),
      mergeMap(({ googleCalendarId }) =>
        from(this.gcalendarService.getGoogleEvents(googleCalendarId)).pipe(
          map((events) =>
            {
              return appointmentsActions.getGoogleEventsSuccess({ events });
            }
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Google Termine konnten nicht geladen werden.", showToast: false, exception: error})))
        )
      )
    )
  );
}
